import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { trackThaiLanguageUsed } from "@/lib/analytics";

type Language = "en" | "th";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (en: string, th: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const translations = {
  en: {
    heroTitle: "Premium Used Cars",
    heroSubtitle: "Find your perfect car with verified quality",
    viewDetails: "View Details",
    chatNow: "Chat Now",
    location: "Location",
    date: "Date",
    miles: "Miles",
    fuel: "Fuel",
    color: "Color",
    trans: "Transmission",
    views: "views",
    km: "km",
    gasoline: "Gasoline",
    diesel: "Diesel",
    electric: "Electric",
    hybrid: "Hybrid",
    manual: "Manual",
    automatic: "Automatic",
    motorproof: "MotorProof ✓ service proof on file",
    year: "Year",
    make: "Make",
    model: "Model",
    trim: "Trim",
    mileage: "Mileage",
    price: "Price",
    district: "District",
    importType: "Import Type",
    official: "Official",
    grey: "Grey",
    bookTestDrive: "BOOK TEST-DRIVE",
    contactSeller: "Contact Seller",
    leaveContact: "Leave Contact",
    requestPrivateShowing: "Request Private Showing",
    engine: "Engine",
    exterior: "Exterior",
    exteriorInterior: "Exterior / Interior",
    description: "Description",
    interior: "Interior",
    fuelType: "Fuel Type",
    notSpecified: "Not specified",
    earlyEditionProgram: "Early Edition Program",
    motorVerifiedBy: "MotorVerified by",
    transShift: "TransShift",
    lineCTA: "LINE",
    feedbackCTA: "Feedback",
    sendFeedback: "Send Feedback",
    shareFeedback: "Share your thoughts about TransShift...",
    cancel: "Cancel",
    submit: "Submit",
    enterFeedback: "Please enter your feedback",
    thankYouFeedback: "Thank you for your feedback!",
    with: "with",
    engineSuffix: "engine",
    transmissionSuffix: "transmission",
    locatedIn: "Located in",
    importSuffix: "import",
    dealerOffer: "Dealer Offer",
    specialCampaign: "Special Campaign",
    validUntil: "Valid until",
    newPrice: "New Price",
    
    // Pre-LINE Dialog - Section Headers
    connectingWithDealer: "Connecting you with the dealer",
    requiredToContact: "Required to contact the dealer",
    helpDealerRespond: "Help the dealer respond faster",
    financingSupport: "Pre-fill for financing support",
    optionalSectionHint: "These details help dealers respond faster",
    optional: "(optional)",
    
    // Pre-LINE Dialog - Required Fields
    copyIdNote: "Fill in the required fields to connect with the dealer",
    copyAndContinue: "Copy message & Open",
    messageLabel: "Message preview",
    pasteHelpNote: "Please paste the text in dealer chat to start the conversation",
    interestedGreeting: "Hello, I am interested in this car",
    contactName: "Name",
    contactPhone: "Phone",
    preferredContactMethod: "Preferred Contact Method",
    purchaseIntentLevel: "Purchase Intent Level",
    
    // Contact Methods
    contactLine: "LINE",
    contactPhonecall: "Phone",
    contactWhatsApp: "WhatsApp",
    
    // Intent Levels
    intentBrowsing: "Just browsing",
    intentShortlisted: "Shortlisted",
    intentReady: "Ready to contact dealer",
    
    // Optional Section 2 - Help dealer respond faster
    budgetRange: "Budget Range",
    purchaseTimeline: "Purchase Timeline",
    tradeInVehicle: "Trade-in Vehicle",
    financingNeeded: "Financing Needed",
    
    // Budget Ranges
    budgetUnder1M: "Under ฿1M",
    budget1to2M: "฿1M - 2M",
    budget2to3M: "฿2M - 3M",
    budget3to5M: "฿3M - 5M",
    budget5MPlus: "฿5M+",
    
    // Purchase Timelines
    timelineThisWeek: "This week",
    timelineThisMonth: "This month",
    timeline1to3Months: "1-3 months",
    timeline3MonthsPlus: "3+ months",
    
    // Yes/No options
    optionYes: "Yes",
    optionNo: "No",
    optionNotSure: "Not sure",
    
    // Optional Section 3 - Financing support
    employmentType: "Employment Type",
    incomeRange: "Income Range",
    monthlyObligations: "Existing Monthly Obligations",
    downPaymentRange: "Down Payment Range",
    
    // Employment Types
    employmentSalaried: "Salaried",
    employmentSelfEmployed: "Self-employed",
    employmentBusinessOwner: "Business owner",
    employmentOther: "Other",
    
    // Income Ranges
    incomeUnder50K: "Under ฿50K/month",
    income50to100K: "฿50K - 100K/month",
    income100to200K: "฿100K - 200K/month",
    income200KPlus: "฿200K+/month",
    
    // Monthly Obligations
    obligationsNone: "None",
    obligationsUnder20K: "Under ฿20K/month",
    obligations20to50K: "฿20K - 50K/month",
    obligations50KPlus: "฿50K+/month",
    
    // Down Payment Ranges
    downPaymentUnder500K: "Under ฿500K",
    downPayment500Kto1M: "฿500K - 1M",
    downPayment1to2M: "฿1M - 2M",
    downPayment2MPlus: "฿2M+",
    
    // Form Validation
    requiredFieldsMissing: "Please fill in all required fields",
    selectOption: "Select...",
    namePlaceholder: "Your name",
    phonePlaceholder: "Your phone number",
    
    // Sell Car Form
    sellCar: "Sell Car",
    sellCarTitle: "Sell Your Car",
    sellCarSubtitle: "Fill in the details and we'll contact you shortly",
    sellCarContactSection: "Contact Information",
    sellCarDetailsSection: "Car Details",
    sellCarAdditionalSection: "Additional Information",
    sellCarName: "Name",
    sellCarPhone: "Phone",
    sellCarLineId: "LINE ID",
    sellCarLineIdPlaceholder: "Your LINE ID",
    sellCarEmail: "Email",
    sellCarEmailPlaceholder: "your@email.com",
    sellCarBrand: "Brand",
    sellCarBrandPlaceholder: "e.g. BMW, Mercedes",
    sellCarModel: "Model",
    sellCarModelPlaceholder: "e.g. 320d, C200",
    sellCarYear: "Year",
    sellCarYearPlaceholder: "e.g. 2021",
    sellCarMileage: "Mileage (km)",
    sellCarMileagePlaceholder: "e.g. 50000",
    sellCarPrice: "Asking Price (฿)",
    sellCarPricePlaceholder: "e.g. 1,500,000",
    sellCarTransmission: "Transmission",
    sellCarFuelType: "Fuel Type",
    sellCarColor: "Color",
    sellCarColorPlaceholder: "e.g. White",
    sellCarLocation: "Location",
    sellCarLocationPlaceholder: "e.g. Bangkok, Nonthaburi",
    sellCarNotes: "Notes",
    sellCarNotesPlaceholder: "Any additional details about your car...",
    sellCarPhotoLinks: "Photo Links",
    sellCarPhotoLinksPlaceholder: "Paste Google Drive or cloud storage links to your car photos",
    sellCarSelectPlaceholder: "Select...",
    sellCarSubmit: "Submit",
    sellCarSubmitting: "Submitting...",
    sellCarSubmitSuccess: "Thank you! We'll contact you soon.",
    sellCarSubmitError: "Something went wrong. Please try again.",
    sellCarNameRequired: "Please enter your name",
    sellCarPhoneRequired: "Please enter your phone number",
    sellCarPhoneInvalid: "Please enter a valid phone number",
    sellCarBrandRequired: "Please enter the car brand",
    sellCarModelRequired: "Please enter the car model",
    sellCarYearRequired: "Please enter the car year",
    sellCarYearInvalid: "Please enter a valid year",
    
    // SEO Text Block
    seoTitle: "Premium Pre-Owned Cars in Bangkok — MotorVerified by TransShift",
    seoIntro: "TransShift curates Bangkok's premium pre-owned cars with verified mileage, authentic service history, and direct-from-dealer transparency. Every listing passes our",
    seoIntro2: "check to ensure trust and clarity for buyers.",
    whyChoose: "Why Choose TransShift?",
    verifiedMileage: "Verified Mileage",
    verifiedMileageDesc: "Every car's odometer reading is validated",
    serviceHistoryCheck: "Service History Check",
    serviceHistoryDesc: "Complete maintenance records verified",
    directFromDealers: "Direct from Dealers",
    directFromDealersDesc: "No intermediaries, transparent pricing",
    motorVerifiedCert: "MotorVerified Certification",
    motorVerifiedCertDesc: "TransShift trust engine validation",
    premiumSelection: "Premium Selection",
    premiumSelectionDesc: "Hand-picked luxury and imported vehicles",
    popularSearches: "Popular Searches",
    popularSearchesText: "Browse our curated selection of verified pre-owned cars including",
    popularSearchesText2: "and more. All listings in prime Bangkok locations including Sukhumvit, Sathorn, and Silom.",
  },
  th: {
    heroTitle: "รถมือสองคุณภาพ",
    heroSubtitle: "ค้นหารถที่ใช่สำหรับคุณด้วยคุณภาพที่รับรอง",
    viewDetails: "ดูรายละเอียด",
    chatNow: "แชทเลย",
    location: "สถานที่",
    date: "วันที่",
    miles: "เลขไมล์",
    fuel: "เชื้อเพลิง",
    color: "สี",
    trans: "ระบบเกียร์",
    views: "ครั้ง",
    km: "กม.",
    gasoline: "น้ำมัน",
    diesel: "ดีเซล",
    electric: "ไฟฟ้า",
    hybrid: "ไฮบริด",
    manual: "ธรรมดา",
    automatic: "อัตโนมัติ",
    motorproof: "MotorProof ✓ มีหลักฐานการเซอร์วิส",
    year: "ปี",
    make: "ยี่ห้อ",
    model: "รุ่น",
    trim: "แบบ",
    mileage: "เลขไมล์",
    price: "ราคา",
    district: "เขต",
    importType: "ประเภทนำเข้า",
    official: "ทางการ",
    grey: "เกรย์",
    bookTestDrive: "ทดสอบขับจริง",
    contactSeller: "ติดต่อผู้ขาย",
    leaveContact: "ฝากข้อมูลติดต่อ",
    requestPrivateShowing: "ติดต่อผู้ขาย",
    engine: "เครื่องยนต์",
    exterior: "สีภายนอก",
    exteriorInterior: "สีภายนอก / ภายใน",
    description: "รายละเอียด",
    interior: "สีภายใน",
    fuelType: "ประเภทเชื้อเพลิง",
    notSpecified: "ไม่ระบุ",
    earlyEditionProgram: "Early Edition",
    motorVerifiedBy: "รับรองโดย",
    transShift: "TransShift",
    lineCTA: "ไลน์",
    feedbackCTA: "ความคิดเห็น",
    sendFeedback: "ส่งความคิดเห็น",
    shareFeedback: "แบ่งปันความคิดเห็นของคุณเกี่ยวกับ TransShift...",
    cancel: "ยกเลิก",
    submit: "ส่ง",
    enterFeedback: "กรุณากรอกความคิดเห็น",
    thankYouFeedback: "ขอบคุณสำหรับความคิดเห็น!",
    with: "เลขไมล์",
    engineSuffix: "เครื่องยนต์",
    transmissionSuffix: "เกียร์",
    locatedIn: "ตั้งอยู่ที่",
    importSuffix: "นำเข้าแบบ",
    dealerOffer: "ข้อเสนอจากดีลเลอร์",
    specialCampaign: "แคมเปญพิเศษ",
    validUntil: "ใช้ได้ถึง",
    newPrice: "ราคาใหม่",
    
    // Pre-LINE Dialog - Section Headers
    connectingWithDealer: "กำลังเชื่อมต่อกับดีลเลอร์",
    requiredToContact: "ข้อมูลที่จำเป็นในการติดต่อดีลเลอร์",
    helpDealerRespond: "ช่วยให้ดีลเลอร์ตอบกลับเร็วขึ้น",
    financingSupport: "ข้อมูลสำหรับการสนับสนุนการผ่อนชำระ",
    optionalSectionHint: "ข้อมูลเหล่านี้ช่วยให้ดีลเลอร์ตอบกลับเร็วขึ้น",
    optional: "(ไม่บังคับ)",
    
    // Pre-LINE Dialog - Required Fields
    copyIdNote: "กรอกข้อมูลที่จำเป็นเพื่อติดต่อกับดีลเลอร์",
    copyAndContinue: "คัดลอกข้อความ & เปิด",
    messageLabel: "ตัวอย่างข้อความ",
    pasteHelpNote: "กรุณาวางข้อความในแชทของดีลเลอร์เพื่อเริ่มการสนทนา",
    interestedGreeting: "สวัสดีครับ/ค่ะ ฉันสนใจรถคันนี้",
    contactName: "ชื่อ",
    contactPhone: "โทร",
    preferredContactMethod: "ช่องทางการติดต่อที่ต้องการ",
    purchaseIntentLevel: "ระดับความสนใจในการซื้อ",
    
    // Contact Methods
    contactLine: "LINE",
    contactPhonecall: "โทรศัพท์",
    contactWhatsApp: "WhatsApp",
    
    // Intent Levels
    intentBrowsing: "ดูเฉยๆ",
    intentShortlisted: "อยู่ในรายการที่สนใจ",
    intentReady: "พร้อมติดต่อดีลเลอร์",
    
    // Optional Section 2 - Help dealer respond faster
    budgetRange: "ช่วงงบประมาณ",
    purchaseTimeline: "กำหนดเวลาซื้อ",
    tradeInVehicle: "มีรถเก่าเทิร์น",
    financingNeeded: "ต้องการสินเชื่อ",
    
    // Budget Ranges
    budgetUnder1M: "ต่ำกว่า ฿1M",
    budget1to2M: "฿1M - 2M",
    budget2to3M: "฿2M - 3M",
    budget3to5M: "฿3M - 5M",
    budget5MPlus: "฿5M+",
    
    // Purchase Timelines
    timelineThisWeek: "สัปดาห์นี้",
    timelineThisMonth: "เดือนนี้",
    timeline1to3Months: "1-3 เดือน",
    timeline3MonthsPlus: "3 เดือนขึ้นไป",
    
    // Yes/No options
    optionYes: "ใช่",
    optionNo: "ไม่",
    optionNotSure: "ไม่แน่ใจ",
    
    // Optional Section 3 - Financing support
    employmentType: "ประเภทการจ้างงาน",
    incomeRange: "ช่วงรายได้",
    monthlyObligations: "ภาระผ่อนรายเดือน",
    downPaymentRange: "ช่วงเงินดาวน์",
    
    // Employment Types
    employmentSalaried: "พนักงานเงินเดือน",
    employmentSelfEmployed: "อาชีพอิสระ",
    employmentBusinessOwner: "เจ้าของธุรกิจ",
    employmentOther: "อื่นๆ",
    
    // Income Ranges
    incomeUnder50K: "ต่ำกว่า ฿50K/เดือน",
    income50to100K: "฿50K - 100K/เดือน",
    income100to200K: "฿100K - 200K/เดือน",
    income200KPlus: "฿200K+/เดือน",
    
    // Monthly Obligations
    obligationsNone: "ไม่มี",
    obligationsUnder20K: "ต่ำกว่า ฿20K/เดือน",
    obligations20to50K: "฿20K - 50K/เดือน",
    obligations50KPlus: "฿50K+/เดือน",
    
    // Down Payment Ranges
    downPaymentUnder500K: "ต่ำกว่า ฿500K",
    downPayment500Kto1M: "฿500K - 1M",
    downPayment1to2M: "฿1M - 2M",
    downPayment2MPlus: "฿2M+",
    
    // Form Validation
    requiredFieldsMissing: "กรุณากรอกข้อมูลที่จำเป็นทั้งหมด",
    selectOption: "เลือก...",
    namePlaceholder: "ชื่อของคุณ",
    phonePlaceholder: "เบอร์โทรศัพท์ของคุณ",
    
    // Sell Car Form
    sellCar: "ขายรถ",
    sellCarTitle: "ขายรถของคุณ",
    sellCarSubtitle: "กรอกรายละเอียด แล้วเราจะติดต่อกลับโดยเร็ว",
    sellCarContactSection: "ข้อมูลการติดต่อ",
    sellCarDetailsSection: "รายละเอียดรถ",
    sellCarAdditionalSection: "ข้อมูลเพิ่มเติม",
    sellCarName: "ชื่อ",
    sellCarPhone: "เบอร์โทร",
    sellCarLineId: "LINE ID",
    sellCarLineIdPlaceholder: "LINE ID ของคุณ",
    sellCarEmail: "อีเมล",
    sellCarEmailPlaceholder: "your@email.com",
    sellCarBrand: "ยี่ห้อ",
    sellCarBrandPlaceholder: "เช่น BMW, Mercedes",
    sellCarModel: "รุ่น",
    sellCarModelPlaceholder: "เช่น 320d, C200",
    sellCarYear: "ปี",
    sellCarYearPlaceholder: "เช่น 2021",
    sellCarMileage: "เลขไมล์ (กม.)",
    sellCarMileagePlaceholder: "เช่น 50000",
    sellCarPrice: "ราคาที่ต้องการ (฿)",
    sellCarPricePlaceholder: "เช่น 1,500,000",
    sellCarTransmission: "ระบบเกียร์",
    sellCarFuelType: "ประเภทเชื้อเพลิง",
    sellCarColor: "สี",
    sellCarColorPlaceholder: "เช่น ขาว",
    sellCarLocation: "พื้นที่",
    sellCarLocationPlaceholder: "เช่น กรุงเทพฯ, นนทบุรี",
    sellCarNotes: "หมายเหตุ",
    sellCarNotesPlaceholder: "รายละเอียดเพิ่มเติมเกี่ยวกับรถของคุณ...",
    sellCarPhotoLinks: "ลิงก์รูปภาพ",
    sellCarPhotoLinksPlaceholder: "วางลิงก์ Google Drive หรือ cloud storage ที่มีรูปรถของคุณ",
    sellCarSelectPlaceholder: "เลือก...",
    sellCarSubmit: "ส่งข้อมูล",
    sellCarSubmitting: "กำลังส่ง...",
    sellCarSubmitSuccess: "ขอบคุณ! เราจะติดต่อกลับเร็วๆ นี้",
    sellCarSubmitError: "เกิดข้อผิดพลาด กรุณาลองใหม่",
    sellCarNameRequired: "กรุณากรอกชื่อ",
    sellCarPhoneRequired: "กรุณากรอกเบอร์โทร",
    sellCarPhoneInvalid: "กรุณากรอกเบอร์โทรที่ถูกต้อง",
    sellCarBrandRequired: "กรุณากรอกยี่ห้อรถ",
    sellCarModelRequired: "กรุณากรอกรุ่นรถ",
    sellCarYearRequired: "กรุณากรอกปีรถ",
    sellCarYearInvalid: "กรุณากรอกปีที่ถูกต้อง",
    
    // SEO Text Block
    seoTitle: "รถมือสองพรีเมียมในกรุงเทพฯ — รับรองโดย MotorVerified จาก TransShift",
    seoIntro: "TransShift คัดสรรรถมือสองพรีเมียมในกรุงเทพฯ ด้วยเลขไมล์ที่ตรวจสอบแล้ว ประวัติการเซอร์วิสที่แท้จริง และความโปร่งใสจากดีลเลอร์โดยตรง ทุกลิสต์ผ่านการตรวจสอบ",
    seoIntro2: "เพื่อสร้างความไว้วางใจและความชัดเจนให้กับผู้ซื้อ",
    whyChoose: "ทำไมต้องเลือก TransShift?",
    verifiedMileage: "เลขไมล์ที่ตรวจสอบแล้ว",
    verifiedMileageDesc: "ค่ามาตรวัดระยะทางของทุกคันได้รับการตรวจสอบ",
    serviceHistoryCheck: "ตรวจสอบประวัติการเซอร์วิส",
    serviceHistoryDesc: "ตรวจสอบบันทึกการบำรุงรักษาที่สมบูรณ์",
    directFromDealers: "ตรงจากดีลเลอร์",
    directFromDealersDesc: "ไม่มีคนกลาง ราคาโปร่งใส",
    motorVerifiedCert: "การรับรอง MotorVerified",
    motorVerifiedCertDesc: "การตรวจสอบจาก TransShift trust engine",
    premiumSelection: "การคัดสรรพรีเมียม",
    premiumSelectionDesc: "คัดสรรรถหรูและรถนำเข้าด้วยมือ",
    popularSearches: "การค้นหายอดนิยม",
    popularSearchesText: "เรียกดูรถมือสองที่ได้รับการตรวจสอบแล้ว รวมถึง",
    popularSearchesText2: "และอื่นๆ ทุกลิสต์อยู่ในทำเลยอดนิยมของกรุงเทพฯ รวมถึงสุขุมวิท สาทร และสีลม",
  },
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>("th");

  // Track Thai language usage whenever language is set to 'th'
  useEffect(() => {
    if (language === 'th') {
      trackThaiLanguageUsed();
    }
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "th" : "en"));
  };

  const t = (en: string, th: string) => {
    return language === "en" ? en : th;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // During HMR, context may temporarily be undefined - return safe fallback
    return {
      language: "en" as const,
      setLanguage: () => {},
      toggleLanguage: () => {},
      t: (en: string, _th: string) => en,
    };
  }
  return context;
}
